/**
 * Ticket-processing capability document.
 *
 * The markdown that says what the platform can do to a ticket in code — tag it, close it,
 * assign it. The SOP → workflow pipeline reads it on every generation, so this document is
 * what decides which SOP steps get pulled out of a workflow graph.
 */

import axios from 'axios';
import api from '@/lib/api';
import { withBuScope } from '@/lib/business-unit-scope';
import type { ApiResponse } from '@/types';

/** A problem the parser found. `error` blocks a save; `warning` is advice. */
export interface CapabilityDocIssue {
	severity: 'error' | 'warning';
	/** 1-based line, so the editor can point at it. */
	line: number;
	message: string;
}

export interface ProcessingCapability {
	id: string;
	phase: 'pre' | 'post';
	label: string;
	description: string;
	keywords: string[];
	params?: Array<{
		name: string;
		label: string;
		type: 'string' | 'string[]' | 'number' | 'boolean' | 'enum';
		required: boolean;
		options?: string[];
	}>;
}

export interface CapabilityDoc {
	content: string;
	/** 0 while the account is still on the built-in list — nothing has been saved yet. */
	version: number;
	edited: boolean;
	capabilities: ProcessingCapability[];
	issues: CapabilityDocIssue[];
	updatedAt?: string;
	updatedBy?: string;
}

export interface CapabilityDocParse {
	capabilities: ProcessingCapability[];
	issues: CapabilityDocIssue[];
}

const BASE = '/processing-capabilities';

/** Thrown when the document has problems that must be fixed before it can be saved. */
export class CapabilityDocInvalid extends Error {
	issues: CapabilityDocIssue[];

	constructor(issues: CapabilityDocIssue[]) {
		super('This document has problems that need fixing first.');
		this.name = 'CapabilityDocInvalid';
		this.issues = issues;
	}
}

/** Thrown when someone else saved while this editor was open. */
export class CapabilityDocConflict extends Error {
	currentVersion: number;

	constructor(currentVersion: number) {
		super('Someone else saved this document. Reload it before saving.');
		this.name = 'CapabilityDocConflict';
		this.currentVersion = currentVersion;
	}
}

export const processingCapabilityService = {
	async get(scope?: { bu_id?: string }): Promise<CapabilityDoc> {
		const response = await api.get<ApiResponse<CapabilityDoc>>(BASE, { params: scope });
		return response.data.data;
	},

	/**
	 * The built-in list, for starting again from the defaults.
	 *
	 * Unscoped, like `preview` below: neither reads or writes a stored document, so there is
	 * no brand for them to belong to. The defaults are the same text for everyone.
	 */
	async defaults(): Promise<{ content: string } & CapabilityDocParse> {
		const response = await api.get<ApiResponse<{ content: string } & CapabilityDocParse>>(`${BASE}/defaults`);
		return response.data.data;
	},

	/** What the document would parse into, without saving it. */
	async preview(content: string): Promise<CapabilityDocParse> {
		const response = await api.post<ApiResponse<CapabilityDocParse>>(`${BASE}/preview`, { content });
		return response.data.data;
	},

	/**
	 * Save the document.
	 *
	 * The two refusals are turned into typed errors rather than a generic failure: one needs
	 * the problems shown against their lines, the other needs a reload, and a single "save
	 * failed" toast serves neither.
	 */
	async save(
		params: { content: string; expectedVersion?: number },
		scope?: { bu_id?: string }
	): Promise<CapabilityDoc> {
		try {
			// The brand rides on the URL so the version check reads the row it is about to
			// write. Checking the account's version before writing a brand's document would
			// compare against a number this brand never edited.
			const response = await api.put<ApiResponse<CapabilityDoc>>(withBuScope(BASE, scope ?? {}), params);
			return response.data.data;
		} catch (error: unknown) {
			if (axios.isAxiosError(error)) {
				const data = error.response?.data as { data?: { issues?: CapabilityDocIssue[]; currentVersion?: number } };
				if (error.response?.status === 422) throw new CapabilityDocInvalid(data?.data?.issues ?? []);
				if (error.response?.status === 409) throw new CapabilityDocConflict(data?.data?.currentVersion ?? 0);
			}
			throw error;
		}
	}
};
